// Azure Translator limits request size by source characters × target languages.
export const AZURE_MAX_CHARS_PER_REQUEST = 45000;

export const TRANSLATION_ERROR_CODES = {
  TOO_LARGE: 400077,
  RATE_LIMIT: 429001,
};

const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

export const chunkTextsByCharBudget = (
  texts,
  languageCount,
  maxChars = AZURE_MAX_CHARS_PER_REQUEST
) => {
  const maxSourceChars = Math.max(1, Math.floor(maxChars / Math.max(languageCount, 1)));
  const chunks = [];
  let currentChunk = {};
  let currentSize = 0;

  Object.entries(texts).forEach(([key, text]) => {
    if (typeof text !== 'string') {
      return;
    }

    if (text.length > maxSourceChars) {
      if (Object.keys(currentChunk).length > 0) {
        chunks.push(currentChunk);
        currentChunk = {};
        currentSize = 0;
      }
      chunks.push({ [key]: text });
      return;
    }

    if (currentSize + text.length > maxSourceChars && Object.keys(currentChunk).length > 0) {
      chunks.push(currentChunk);
      currentChunk = {};
      currentSize = 0;
    }

    currentChunk[key] = text;
    currentSize += text.length;
  });

  if (Object.keys(currentChunk).length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};

export class TranslationError extends Error {
  constructor(message, { code, batchLabel } = {}) {
    super(message);
    this.name = 'TranslationError';
    this.code = code;
    this.batchLabel = batchLabel;
  }
}

export const parseAzureTranslateError = (error, batchLabel) => {
  const code = Number(error?.code);
  const message = error?.error || error?.message || 'Translation failed';

  if (code === TRANSLATION_ERROR_CODES.TOO_LARGE || /maximum request size/i.test(message)) {
    return new TranslationError(
      'The listing text is too long to translate. Please reduce the content and try again.',
      { code: TRANSLATION_ERROR_CODES.TOO_LARGE, batchLabel }
    );
  }

  if (
    code === TRANSLATION_ERROR_CODES.RATE_LIMIT ||
    error?.status === 429 ||
    /exceeded request limits/i.test(message)
  ) {
    return new TranslationError(
      'Translation service is temporarily busy. Please wait a few seconds and try again.',
      { code: TRANSLATION_ERROR_CODES.RATE_LIMIT, batchLabel }
    );
  }

  return new TranslationError(message, { code, batchLabel });
};

export const translateTextsInBatches = async (
  azureTranslateFn,
  texts,
  languages,
  { batchLabel } = {}
) => {
  if (!texts || Object.keys(texts).length === 0) {
    return null;
  }

  const chunks = chunkTextsByCharBudget(texts, languages.length);
  const merged = {};

  for (let i = 0; i < chunks.length; i += 1) {
    try {
      const response = await azureTranslateFn({
        texts: chunks[i],
        targetLanguages: languages,
      });
      Object.assign(merged, response);
    } catch (error) {
      throw parseAzureTranslateError(error, batchLabel);
    }
  }

  return merged;
};
